import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addChecklistItem,
  addPilotPeople,
  assignPeople,
  createBucket,
  createPilot,
  getBucket,
  getBucketOptions,
  getBuckets,
  getReps,
  getSuggestions,
  getTeam,
  importBucketList,
  removeChecklistItem,
  removePilot,
  removePilotPerson,
  searchCompanies,
  setCompanyLanguage,
  setRepRole,
  updateBucket,
  updateChecklistItem,
  updatePilotPerson,
  type BucketUpdate,
  type ChoosableLanguage,
  type RepSetting,
  type ImportRow,
  type NewPilotPerson,
} from '../api/pilots';

// lemlist syncs hourly; five minutes keeps an open page close without hammering the API.
const REFRESH_MS = 300_000;

function useInvalidatePilots() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['pilots'] });
}

// ── Buckets ─────────────────────────────────────────────────────────────────

export function useBuckets() {
  return useQuery({ queryKey: ['pilots', 'buckets'], queryFn: getBuckets, refetchInterval: REFRESH_MS });
}

export function useBucket(id: string | undefined) {
  return useQuery({ queryKey: ['pilots', 'buckets', id], queryFn: () => getBucket(id as string), enabled: !!id, refetchInterval: REFRESH_MS });
}

export function useBucketOptions(enabled: boolean) {
  return useQuery({ queryKey: ['pilots', 'buckets', 'options'], queryFn: getBucketOptions, enabled, staleTime: 600_000 });
}

export function useCreateBucket() {
  const invalidate = useInvalidatePilots();
  return useMutation({ mutationFn: createBucket, onSuccess: invalidate });
}

export function useUpdateBucket(id: string) {
  const invalidate = useInvalidatePilots();
  return useMutation({ mutationFn: (body: BucketUpdate) => updateBucket(id, body), onSuccess: invalidate });
}

export function useChecklist(bucketId: string) {
  const invalidate = useInvalidatePilots();
  const add = useMutation({ mutationFn: (label: string) => addChecklistItem(bucketId, label), onSuccess: invalidate });
  const update = useMutation({
    mutationFn: ({ itemId, ...body }: { itemId: string; done?: boolean; ownerEmail?: string | null }) => updateChecklistItem(bucketId, itemId, body),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (itemId: string) => removeChecklistItem(bucketId, itemId), onSuccess: invalidate });
  return { add, update, remove };
}

export function useTeam() {
  return useQuery({ queryKey: ['pilots', 'team'], queryFn: getTeam, staleTime: 3_600_000 });
}

// ── Companies and people ────────────────────────────────────────────────────

export function useSetRepRole() {
  const invalidate = useInvalidatePilots();
  return useMutation({ mutationFn: ({ email, role }: { email: string; role: RepSetting | null }) => setRepRole(email, role), onSuccess: invalidate });
}

export function useReps() {
  return useQuery({ queryKey: ['pilots', 'reps'], queryFn: getReps, staleTime: 3_600_000 });
}

export function useCompanySearch(q: string) {
  return useQuery({ queryKey: ['pilots', 'companies', q], queryFn: () => searchCompanies(q), enabled: q.trim().length >= 2 });
}

export function useSuggestions(id: string, enabled: boolean) {
  return useQuery({ queryKey: ['pilots', id, 'suggestions'], queryFn: () => getSuggestions(id), enabled });
}

export function useCreatePilot(bucketId: string) {
  const invalidate = useInvalidatePilots();
  return useMutation({
    mutationFn: (body: { companyName: string; companyDomain: string | null; attioCompanyId: string | null }) => createPilot(bucketId, body),
    onSuccess: invalidate,
  });
}

export function useSetCompanyLanguage(id: string) {
  const invalidate = useInvalidatePilots();
  return useMutation({ mutationFn: (language: ChoosableLanguage | null) => setCompanyLanguage(id, language), onSuccess: invalidate });
}

export function useRemovePilot() {
  const invalidate = useInvalidatePilots();
  return useMutation({ mutationFn: removePilot, onSuccess: invalidate });
}

export function useAddPilotPeople(id: string) {
  const invalidate = useInvalidatePilots();
  return useMutation({ mutationFn: (people: NewPilotPerson[]) => addPilotPeople(id, people), onSuccess: invalidate });
}

export function useImportList(bucketId: string) {
  const invalidate = useInvalidatePilots();
  return useMutation({ mutationFn: (rows: ImportRow[]) => importBucketList(bucketId, rows), onSuccess: invalidate });
}

export function useAssignPeople(bucketId: string) {
  const invalidate = useInvalidatePilots();
  return useMutation({
    mutationFn: ({ personIds, assigneeEmail }: { personIds: string[]; assigneeEmail: string | null }) => assignPeople(bucketId, personIds, assigneeEmail),
    onSuccess: invalidate,
  });
}

export function useUpdatePilotPerson(id: string) {
  const invalidate = useInvalidatePilots();
  return useMutation({
    mutationFn: ({ personId, ...body }: { personId: string; assigneeEmail?: string | null; manualCall?: boolean; manualMeeting?: boolean }) =>
      updatePilotPerson(id, personId, body),
    onSuccess: invalidate,
  });
}

export function useRemovePilotPerson(id: string) {
  const invalidate = useInvalidatePilots();
  return useMutation({ mutationFn: (personId: string) => removePilotPerson(id, personId), onSuccess: invalidate });
}
